import './styles.scss';

import { faArrowsFromLine } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@mui/material';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { BackgroundMessage } from '../../../../../components/background-message';
import { DownloadButton } from '../../../../../components/download-button';
import { ReportFilterButtons } from '../../../../../components/report-filter-buttons';
import { ReportFilterMultiSelect } from '../../../../../components/report-filter-buttons/report-filter-multi-select';
import Select from '../../../../../components/select/select';
import { PORTFOLIO_TABS_NAME } from '../../../../../constants/reports/reports-data';
import {
  PORTFOLIO_CONTRACTS,
  RISK_TOKEN_LIST,
  TIME_LIST,
  VOL_SHOCK_REAL_RISK,
} from '../../../../../constants/reports/reports-filters';
import { PORTFOLIO_MESSAGES } from '../../../../../constants/reports/reports-messages';
import { GlobalContext } from '../../../../../providers/global-provider';
import {
  DATA_DISPLAY_KEYS,
  PortfolioManagementContext,
} from '../../../../../providers/portfolio-management';
import { RiskFiltersContext } from '../../../../../providers/risk-management/risk-filters-provider';
import {
  RiskManagementContext,
  riskTypes,
} from '../../../../../providers/risk-management/risk-management-provider';
import { IAccountOption } from '../../../../../providers/venue-provider';
import Utils from '../../../../../utils/immersive_utils';
import { formatTokenPrice } from '../../../../../utils/decimal-map';
import { CheckLastUpdate } from '../../../components/check-last-update';
import { CheckToggleLabels } from '../../../components/check-toggle-labels';
import { useIsSpotLoaded } from '../../../hooks/risk/use-is-spot-loaded';
import { useRiskData } from '../../../hooks/risk/use-risk-data';
import { useFreezeSpotValues } from '../../../hooks/use-freeze-spot-values';
import { DropdownListRiskSnapshots } from '../components/dropdown-list-risk-snapshots';
import ViewSettings from '../view-settings';

import { collectNotificationsFromTokenMap } from './collect-notifications';
import RiskGraphs from './risk-graphs';
import RiskTable from './risk-table';
import { AutoUpdateTimer } from '../../../components/auto-update-timer';
import { ToggleSwitch } from '../../../../../components/toggle-switch';
import { getObscuredProps } from '../../../../../hooks/use-session-tracker';
import { BookErrorsMessage } from '../../new-pnl/book-errors-message';

interface iProps {
  matrix: 'SPOT_LADDER' | 'SPOT_GRID' | 'SPOT_TIME';
  accountOption: IAccountOption;
}

export interface SpotLabel {
  label: string;
  labelKey: string;
}

export const RiskManagementData = ({ matrix, accountOption }: iProps) => {
  const { portfolioCurrentTab } = useContext(GlobalContext);
  const {
    getDataSpotFromAccount,
    getAggregatedDataSpot,
    aggregatedData,
    selectedDataDisplayOption,
    riskSettingsSelected,
  } = useContext(PortfolioManagementContext);
  const {
    filters: {
      slider,
      setSlider,
      token,
      setToken,
      tokenList,
      riskMetric,
      setRiskMetric,
      setVenueType,
      riskToken,
      setRiskToken,
      volShock,
      setVolShock,
      time,
      setTime,
      timeList,
      volShockList,
      riskMetricList,
      setRiskMetricList,
      riskInstType,
      setRiskInstType,
      error,
      setError,
    },
  } = useContext(RiskFiltersContext);

  const {
    getData,
    riskLoadings: {
      isSpotLadderQuoteLoading,
      isSpotLadderBaseLoading,
      isSpotGridQuoteLoading,
      isSpotGridBaseLoading,
      isSpotTimeQuoteLoading,
      isSpotTimeBaseLoading,
    },
    data,
    dataGraph,
    setData,
    isRiskAllSpotLoading,
    isAutoUpdate,
    setIsAutoUpdate,
    isLoadingByParams,
    loadingByRequestId,
  } = useContext(RiskManagementContext);

  const [lastUpdateSpot, setLastUpdateSpot] = useState<string>('');
  const [spotLabels, setSpotLabels] = useState<SpotLabel[]>([]);
  const [autoUpdateTrigger, setAutoUpdateTrigger] = useState(0);
  const [alertVisible, setAlertVisible] = useState(false);
  const [bookErrors, setBookErrors] = useState<Array<Record<string, string[]>>>([]);

  const { persistSpotValues } = useFreezeSpotValues();

  const riskTableRef = useRef();

  const isFreezeSpot = spotLabels.some((item) => item.labelKey === 'freezeSpot');
  const isRealRisk =
    !isFreezeSpot && volShock === VOL_SHOCK_REAL_RISK.value && time === TIME_LIST[0].value;

  const isDataDisplayPerAccount = selectedDataDisplayOption === DATA_DISPLAY_KEYS.perAccount;

  const { RISK } = PORTFOLIO_CONTRACTS;

  const { header, handleData, spotValuesRef } = useRiskData({ matrix });
  const { isSpotLoaded } = useIsSpotLoaded();

  const isSpotLadder =
    portfolioCurrentTab === riskTypes.spotLadder && matrix === riskTypes.spotLadder;

  const handleDrilldrown = (expand: boolean) => {
    if (riskTableRef.current) {
      (riskTableRef.current as any).handleExpandableOrCollapse(expand);
    }
  };

  const aggregatedAccountsByExchange = useMemo(() => {
    return aggregatedData.accounts.reduce((acc: any, account: any) => {
      const exchange = account.exchange;
      if (['deribit', 'okx', 'otc'].includes(exchange)) {
        if (!acc[exchange]) {
          acc[exchange] = [];
        }
        acc[exchange].push(account);
      }
      return acc;
    }, {});
  }, [aggregatedData.accounts]);

  const handleRiskMetricList = (riskMetrics: any) => {
    setRiskMetricList((prev: any) =>
      prev.map((vs: any) => ({
        ...vs,
        isVisible: !!riskMetrics.find(
          (riskMetric: any) => riskMetric.toString() === vs.value && vs.value !== 'PNL',
        ),
      })),
    );
  };

  const handleSpotLabels = (dataAccount: any, spotValues: number[]) => {
    const { isSpotOverride, isFreezeSpotOverride, isOtcTraderVols } = dataAccount;
    const labels: SpotLabel[] = [];

    if (!isFreezeSpotOverride) {
      labels.push({
        label: isSpotOverride ? '00:00 UTC Spot' : 'Live Spot',
        labelKey: isSpotOverride ? 'spotOverride' : 'liveSpot',
      });
    }

    if (isFreezeSpotOverride) {
      const freezeSpotIndex = 23;
      const freezeSpotValue = spotValues[freezeSpotIndex];
      const freezeSpotValueFormated = formatTokenPrice(Number(freezeSpotValue), token ?? '') || '';

      labels.push({
        label: `Fixed Spot: ${freezeSpotValueFormated}`,
        labelKey: 'freezeSpot',
      });
    }

    labels.push({
      label: isOtcTraderVols ? 'OTC Trader Vols' : 'Immersive Vols',
      labelKey: isOtcTraderVols ? 'otcTraderVols' : 'immersiveVols',
    });

    setSpotLabels(labels);
  };

  useEffect(() => {
    if (!isDataDisplayPerAccount || portfolioCurrentTab !== matrix) return;

    if (
      !isSpotLoaded(matrix, {
        accountOption,
        exchange: accountOption?.exchange,
        riskToken,
      })
    )
      return;

    const dataAccount = getDataSpotFromAccount(accountOption, matrix, riskToken);

    if (!dataAccount || !dataAccount.data) {
      getData({
        account: accountOption,
        dataParent: matrix,
        riskTokenParam: riskToken,
        riskSettings: riskSettingsSelected,
      });
      return;
    }

    handleRiskMetricList(dataAccount.riskMetrics);

    const getGroupedAccountsData = () => {
      setData([]);
      setError(false);

      if (dataAccount.error) {
        setError(true);
        setBookErrors([]);
        return null;
      }

      if (dataAccount.lastUpdate) {
        setLastUpdateSpot(dataAccount.lastUpdate);
      }

      setVenueType(dataAccount.exchange);
      setRiskToken(riskToken);

      const riskData = dataAccount.data;

      return {
        data: {
          [dataAccount.exchange]: riskData,
        },
      };
    };

    const groupedAccountsData = getGroupedAccountsData();

    if (!groupedAccountsData) return;

    const hasNonEmptyResult = (exchangeData: any) =>
      exchangeData &&
      Object.keys(exchangeData).length > 0 &&
      exchangeData.result &&
      Object.keys(exchangeData.result).length > 0;

    const isAtLeastOneNotEmptyData = Object.values(groupedAccountsData.data).some(
      hasNonEmptyResult,
    );

    if (!isAtLeastOneNotEmptyData) return;

    const exchangeData = dataAccount.data;

    persistSpotValues(exchangeData?.result);

    const resultMap = exchangeData?.result ?? {};

    // A reported pair carries report blocks (array shape, report on the first
    // entry). A notification-only / dict-shaped (excluded) pair does not, and
    // must never become the spot-axis source - otherwise result[token][0].report
    // is undefined and the grid/graph silently loses its spot axis (IM-2299).
    const isReportedEntry = (entry: unknown): boolean =>
      Array.isArray(entry) && !!(entry[0] as { report?: unknown })?.report;

    if (!isReportedEntry(resultMap[token])) {
      const firstReportedToken = Object.keys(resultMap).find((key) =>
        isReportedEntry(resultMap[key]),
      );
      // Switch to the first pair that actually carries reports. If none do (all
      // pairs excluded), fall through so the excluded notifications still render
      // below without a broken spot axis.
      if (firstReportedToken && firstReportedToken !== token) {
        setToken(firstReportedToken);
        return;
      }
    }

    const spotValues: number[] = resultMap[token]?.[0]?.report?.spot;

    handleSpotLabels(dataAccount, spotValues);

    const perAccountErrors = collectNotificationsFromTokenMap(exchangeData?.result);
    setBookErrors(perAccountErrors);

    if (spotValues && spotValues.length > 0) {
      spotValuesRef.current = spotValues;
      handleData(groupedAccountsData, dataAccount.riskMetrics);
    }
  }, [
    handleData,
    time,
    token,
    volShock,
    riskToken,
    accountOption,
    isSpotLadderQuoteLoading,
    isSpotLadderBaseLoading,
    isSpotGridQuoteLoading,
    isSpotGridBaseLoading,
    isSpotTimeQuoteLoading,
    isSpotTimeBaseLoading,
    getData,
    isDataDisplayPerAccount,
    portfolioCurrentTab,
    autoUpdateTrigger,
    loadingByRequestId,
    isLoadingByParams,
  ]);

  useEffect(() => {
    if (isDataDisplayPerAccount || portfolioCurrentTab !== matrix || !isSpotLoaded(matrix)) return;

    const dataAccountAggregated = getAggregatedDataSpot(matrix, riskToken);

    if (dataAccountAggregated?.riskMetrics) {
      handleRiskMetricList(dataAccountAggregated.riskMetrics);
    }

    const isSpotCalculateAggregatedAccounts = true;

    if (!dataAccountAggregated?.data) {
      Object.keys(aggregatedAccountsByExchange).forEach((exchange) => {
        const accounts = aggregatedAccountsByExchange[exchange];
        if (accounts.length > 0) {
          getData({
            account: accounts,
            dataParent: matrix,
            riskTokenParam: riskToken,
            riskSettings: riskSettingsSelected,
            isSpotAggregatedAccounts: isSpotCalculateAggregatedAccounts,
            accountsAggregated: aggregatedData.accounts,
          });
        }
      });
      return;
    }

    const getAggregatedAccountsData = () => {
      setData([]);
      setError(false);

      const hasError = Object.values(dataAccountAggregated.error || {}).some(Boolean);
      if (hasError) {
        setError(true);
        setBookErrors([]);
        return null;
      }

      setLastUpdateSpot(dataAccountAggregated.lastUpdate);
      setRiskToken(riskToken);

      const exchangeDataFirstValue = Object.values(dataAccountAggregated.data)[0];
      persistSpotValues(exchangeDataFirstValue);

      const data: any = {};
      Object.keys(aggregatedAccountsByExchange).forEach((exchange) => {
        data[exchange] = dataAccountAggregated.data[exchange] || {};
      });

      return { data };
    };

    const groupedAccountsDataAggregated = getAggregatedAccountsData();

    if (!groupedAccountsDataAggregated) return;

    const hasTokenData = Object.keys(dataAccountAggregated.data || {}).some(
      (exchange) => dataAccountAggregated.data[exchange]?.[token],
    );

    if (!hasTokenData) return;

    let spotValues: number[] = [];
    for (const exchange of Object.keys(dataAccountAggregated.data || {})) {
      const tokenData = dataAccountAggregated.data[exchange]?.[token];
      if (tokenData) {
        spotValues = tokenData[0]?.report?.spot;
        break;
      }
    }

    if (spotValues && spotValues.length > 0) {
      spotValuesRef.current = spotValues;
      handleSpotLabels(dataAccountAggregated, spotValues);
      handleData(groupedAccountsDataAggregated, dataAccountAggregated.riskMetrics);
    }

    const aggregatedErrors: Array<Record<string, string[]>> = [];
    Object.values(dataAccountAggregated.data || {}).forEach((exchangeObj: any) => {
      const tokenMap =
        exchangeObj?.result && typeof exchangeObj.result === 'object'
          ? exchangeObj.result
          : exchangeObj;
      aggregatedErrors.push(...collectNotificationsFromTokenMap(tokenMap));
    });

    setBookErrors(aggregatedErrors);
  }, [
    handleData,
    time,
    token,
    volShock,
    riskToken,
    matrix,
    isSpotLadderQuoteLoading,
    isSpotLadderBaseLoading,
    isSpotGridQuoteLoading,
    isSpotGridBaseLoading,
    isSpotTimeQuoteLoading,
    isSpotTimeBaseLoading,
    getData,
    isDataDisplayPerAccount,
    portfolioCurrentTab,
    aggregatedAccountsByExchange,
    loadingByRequestId,
  ]);

  const handleOnDownload = useCallback(() => {
    const csvData: any[] = [];

    const handleDataChildren = (data: any) => {
      data.forEach((element: any) => {
        if (Array.isArray(element)) {
          handleDataChildren(element);
        } else if (element && element.values && element.values.length > 0) {
          let values = element.values;

          if (slider.length > 0) {
            values = element.values.filter((value: any, index: number) => {
              const columnValue = header[index];
              return slider[0] <= columnValue && slider[1] >= columnValue;
            });
          }

          csvData.push([element.label, ...values]);

          if (element.children && element.children.length > 0) {
            handleDataChildren(element.children);
          }
        }
      });
    };

    handleDataChildren(data);

    const exchangesAndAccounts = [`${accountOption.exchange}-${accountOption.name}`];
    const date = new Date().toISOString();

    const headerConfig =
      slider.length > 0
        ? header
            .filter((column) => slider[0] <= column && slider[1] >= column)
            .map((column) => `${column.toFixed(2)}%`)
        : header.map((column) => `${column.toFixed(2)}%`);

    const filename = `${exchangesAndAccounts.join(', ')}-${token}-${riskToken}-${date}.csv`;
    const csvHeaders = ['shock', ...headerConfig];

    Utils.downloadToCsv(filename, csvData, csvHeaders);
  }, [data, header, token, riskToken, slider, accountOption]);

  const renderBackgroundMessage = useCallback(
    (loading: boolean, error: boolean, matrix: any) => {
      if (loading) {
        return (
          <div className="risk-loading">
            <BackgroundMessage isLoading />
          </div>
        );
      }
      if (error) {
        return (
          <BackgroundMessage
            message={PORTFOLIO_MESSAGES.ERROR_REQUEST_RISK}
            type="error"
            transparent
            button={{
              title: PORTFOLIO_MESSAGES.ERROR_REQUEST_RISK.button,
              onClick: () => {},
            }}
          />
        );
      }

      if (!data || data.length === 0) {
        return (
          <div className="risk-empty-data">
            <BackgroundMessage message={PORTFOLIO_MESSAGES.NO_DATA_FOUND} />
          </div>
        );
      }

      return (
        <RiskTable
          data={data}
          header={header}
          columnsFilter={slider}
          ref={riskTableRef}
          riskType={matrix}
          accountOption={accountOption}
          currentTab={portfolioCurrentTab}
          spotLabels={spotLabels}
        />
      );
    },
    [data, error, header, slider, riskTableRef, portfolioCurrentTab, spotLabels],
  );

  const loadingStateMapping: any = {
    BASE: {
      [PORTFOLIO_TABS_NAME.spotLadder]: isSpotLadderBaseLoading,
      [PORTFOLIO_TABS_NAME.spotGrid]: isSpotGridBaseLoading,
      [PORTFOLIO_TABS_NAME.spotTime]: isSpotTimeBaseLoading,
    },
    QUOTE: {
      [PORTFOLIO_TABS_NAME.spotLadder]: isSpotLadderQuoteLoading,
      [PORTFOLIO_TABS_NAME.spotGrid]: isSpotGridQuoteLoading,
      [PORTFOLIO_TABS_NAME.spotTime]: isSpotTimeQuoteLoading,
    },
  };

  const renderRiskTableData = useMemo(() => {
    if (riskToken === 'BASE' || riskToken === 'QUOTE') {
      const loadingStates = loadingStateMapping[riskToken];
      const currentLoading = loadingStates[matrix];

      const loadingByParams = isDataDisplayPerAccount
        ? isLoadingByParams({
            exchange: accountOption.exchange,
            riskToken,
            matrix,
            account: accountOption.name,
          })
        : currentLoading;

      if (currentLoading !== undefined) {
        return renderBackgroundMessage(loadingByParams, error, matrix);
      }
    }
    return null;
  }, [
    isSpotLadderBaseLoading,
    isSpotGridBaseLoading,
    isSpotTimeBaseLoading,
    isSpotLadderQuoteLoading,
    isSpotGridQuoteLoading,
    isSpotTimeQuoteLoading,
    matrix,
    riskToken,
    renderBackgroundMessage,
    error,
    accountOption,
    isLoadingByParams,
    isDataDisplayPerAccount,
  ]);

  const optionsMapping = {
    [riskTypes.spotLadder]: [
      {
        titleFilter: 'Vol Shock',
        dataArr: volShockList,
        selected: volShock,
        setSelected: setVolShock,
      },
      {
        titleFilter: 'Time',
        dataArr: timeList,
        selected: time,
        setSelected: setTime,
      },
    ],
    [riskTypes.spotGrid]: [
      {
        titleFilter: 'Risk Metric',
        dataArr: riskMetricList,
        selected: riskMetric,
        setSelected: setRiskMetric,
      },
    ],
    [riskTypes.spotTime]: [
      {
        titleFilter: 'Risk Metric',
        dataArr: riskMetricList,
        selected: riskMetric,
        setSelected: setRiskMetric,
      },
    ],
  };

  const renderOptions = useMemo(() => {
    if (!isSpotLoaded(matrix)) {
      return null;
    }

    const options = optionsMapping[matrix];
    if (!options) {
      return null;
    }

    return (
      <>
        {options.map((option, index) => (
          <ReportFilterButtons
            key={index}
            titleFilter={option.titleFilter}
            dataArr={option.dataArr}
            selected={option.selected}
            setSelected={option.setSelected}
          />
        ))}
      </>
    );
  }, [riskMetric, riskMetricList, time, timeList, volShock, volShockList, matrix, isSpotLoaded]);

  const renderTokenSelect = useMemo(() => {
    const visibleTokenList = tokenList.filter((tk: any) => tk.isVisible);

    const selectedToken =
      visibleTokenList.find((tk: any) => tk.value === token) || visibleTokenList[0];

    const tokenListMapp = visibleTokenList.map((tk: any) => ({
      value: tk.value,
      name: tk.label,
    }));

    if (!selectedToken || !selectedToken?.value) return <></>;

    return (
      <Select
        defaultValue={{ value: selectedToken?.value, name: selectedToken?.label }}
        values={tokenListMapp}
        onChange={(val: string) => {
          setToken(val);
        }}
        className="token-select"
        label="Token"
        disabled={isRiskAllSpotLoading}
      />
    );
  }, [tokenList, token, setToken, isRiskAllSpotLoading]);

  const renderRiskGraphs = useMemo(() => {
    if (!riskSettingsSelected?.showRiskCharts || !isSpotLadder) {
      return null;
    }

    let minIndex = -1;
    let maxIndex = -1;

    if (header && header.length && slider && slider.length === 2) {
      minIndex = header.findIndex((value) => value === slider[0]);
      maxIndex = header.findIndex((value) => value === slider[1]);
    }

    if (!slider || slider.length !== 2) {
      minIndex = 0;
      maxIndex = header.length - 1;
    }

    if (
      !data ||
      data.length === 0 ||
      !dataGraph ||
      dataGraph.length === 0 ||
      minIndex === -1 ||
      maxIndex === -1
    ) {
      return null;
    }

    const isLoading = isSpotLadderQuoteLoading || isSpotLadderBaseLoading;

    return (
      <RiskGraphs
        riskMetricList={riskMetricList}
        currency={token}
        minIndex={minIndex}
        maxIndex={maxIndex}
        isLoading={isLoading}
      />
    );
  }, [
    header,
    data,
    token,
    riskSettingsSelected?.showRiskCharts,
    riskMetricList,
    portfolioCurrentTab,
    slider,
    dataGraph,
    isSpotLadderQuoteLoading,
    isSpotLadderBaseLoading,
  ]);

  return (
    <div className="risk-management-data">
      <div className="risk-header">
        {tokenList.length > 0 && renderTokenSelect}

        {RISK_TOKEN_LIST.length > 0 && (
          <ReportFilterButtons
            titleFilter="Risk Token"
            dataArr={RISK_TOKEN_LIST}
            selected={riskToken}
            setSelected={setRiskToken}
            disabled={!isSpotLoaded(matrix)}
          />
        )}

        <ReportFilterMultiSelect
          titleFilter="Inst Type"
          dataArr={RISK.CONTRACT_KEYS}
          selected={riskInstType}
          setSelected={setRiskInstType}
          disabled={!isSpotLoaded(matrix)}
        />

        {renderOptions}

        {matrix === riskTypes.spotLadder && (
          <div className={`risk-status ${isRealRisk ? 'real' : 'simulation'}`}>
            {isRealRisk ? 'Real Risk' : 'Simulation'}
          </div>
        )}
        <div className="view-settings-container">
          <Button
            className={`expand-btn`}
            onClick={() => {
              handleDrilldrown(true);
            }}
          >
            <FontAwesomeIcon icon={faArrowsFromLine} />
            <div>Expand all</div>
          </Button>
          <Button
            className={`expand-btn`}
            onClick={() => {
              handleDrilldrown(false);
            }}
          >
            <FontAwesomeIcon icon={faArrowsFromLine} />
            <div> Collapse all</div>
          </Button>
          <ViewSettings setSlider={setSlider} />
        </div>
        {isDataDisplayPerAccount && <DownloadButton onClick={handleOnDownload} />}
      </div>

      {isSpotLoaded(riskTypes.spotLadder) && (
        <div className="risk-management-check-infos">
          <CheckToggleLabels labels={spotLabels} />

          <div className="risk-management-check-infos-update">
            {isSpotLadder && isDataDisplayPerAccount && (
              <div className="risk-management-check-infos-update-toggle">
                <ToggleSwitch
                  isOn={isAutoUpdate}
                  setIsOn={() => setIsAutoUpdate((prev) => !prev)}
                />
                <span>Auto-update</span>
              </div>
            )}

            {isSpotLadder && selectedDataDisplayOption === DATA_DISPLAY_KEYS.perAccount ? (
              <DropdownListRiskSnapshots timestamp={lastUpdateSpot} />
            ) : (
              <CheckLastUpdate lastUpdate={lastUpdateSpot} />
            )}

            {matrix === riskTypes.spotLadder && isAutoUpdate && isDataDisplayPerAccount && (
              <AutoUpdateTimer
                onUpdate={async () => {
                  await getData({
                    account: accountOption,
                    dataParent: riskTypes.spotLadder,
                    riskTokenParam: riskToken,
                    riskSettings: riskSettingsSelected,
                    autoUpdate: true,
                  });

                  setAutoUpdateTrigger((prev) => prev + 1);
                }}
              />
            )}
          </div>
        </div>
      )}

      {isSpotLoaded(matrix) && (
        <BookErrorsMessage
          alertVisible={alertVisible}
          setAlertVisible={setAlertVisible}
          bookErrors={bookErrors}
        />
      )}
      <div className="risk-management-content">{renderRiskTableData}</div>
      {isDataDisplayPerAccount && (
        <div className="risk-management-graph" {...getObscuredProps()}>
          {renderRiskGraphs}
        </div>
      )}
    </div>
  );
};
